import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, inject, ChangeDetectorRef } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router} from "@angular/router";
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EntityList } from '../../objects/entity-list';
import { EntityType } from '../../objects/interface-library';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { AgGridModule } from 'ag-grid-angular';

import { ViewsService } from '../../service/views.service';
import { PartyService } from '../../service/entity-services/party.service';

import { GenericListOfSavedReportsDialog } from '../toolbar-utilities/view-utilities';

import * as partyColumns from "../../../app/shared/entity-columns/parties-columns";
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { NewWorkflowRequestButtonComponent } from '../../shared/components/new-workflow-request-button/new-workflow-request-button.component';



@Component({
    selector: 'app-parties',
    templateUrl: './parties.component.html',
    styleUrls: ['./parties.component.css'],
    standalone: true,
    imports: [
      AsyncPipe,
      AgGridModule,
      MatButtonModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      FormsModule,
      ReactiveFormsModule,
      MatGridListModule,
      MatDividerModule,
      MatSidenavModule,
      MatProgressSpinner,
      MatAutocompleteModule,
      MatChipsModule,
      MatIconModule,
      NewWorkflowRequestButtonComponent
    ]
  })
export class PartiesComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {

  private cdr = inject(ChangeDetectorRef);

  @ViewChild('partiesSidenavActions') partiesSidenavActions!: TemplateRef<any>;

  showNameSavedView = false;  // shows the dialog to save a named view
  nameOfSavedView = null; // if you are showing the ability to name a view what name did the customer type
  loadSavedReportVisible = false; // Shows the select drop down to load a saved report
  isInitializing = true; // For loading screen

  columnDefs = null;

  defaultUserViewCols = [
      partyColumns.colVendorNum,
      partyColumns.colVendorName,
      partyColumns.colAddress,
      partyColumns.colParentParty,
      partyColumns.colAnnualRevenues,
      partyColumns.colRiskScore,
      partyColumns.colDescription
  ];

  selectedCols = this.defaultUserViewCols;

  physicianCols: any[] = [
    partyColumns.colVendorName,
    partyColumns.colNpiNum,
    partyColumns.colParentParty,
    partyColumns.colStanding,
    partyColumns.colFormallyKnownAs,
    partyColumns.colDba,
    partyColumns.colWebsite
  ]

  userCurrentView = null;

  showPhysicianDirectory = false;

  filteredPhysicians = [];

  // Column customization properties
  availableColumns: any[] = [];
  availableColumnSelectList: {value: any, viewValue: string}[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{value: any, viewValue: string}[]>;

  // AG-Grid default column configuration
  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  };
  public showFloatingFilters = false;

  constructor(public router: Router,
              public savedViewsDialog: MatDialog,
              private sidenavContentService: SidenavContentService) {

    super();
    this.entityType = 'parties'; // Set the entity type for view management
    this.buildAvailableColumns();
    this.initializeSessionStorage();

    this.allParties$ = this.partyService.allParties$;
    this.partyService.refreshAllPartiesRecords();

  }


  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Parties');

    // NOTE: View loading is now deferred to after grid is ready (see completeInitialization)

    // Set up autocomplete filtering for column selector
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  /**
   * Override onGridReady to complete initialization after grid is ready
   */
  override onGridReady(params: any): void {
    // Call parent class onGridReady first
    super.onGridReady(params);

    // Complete initialization behind the loading screen
    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false; // Hide loading screen on error
    });
  }

  /**
   * Completes the grid initialization after grid is ready.
   * Uses official AG Grid APIs for state management.
   * Priority: Session Storage > Saved Views > Auto-size
   */
  private async completeInitialization(): Promise<void> {
    // CRITICAL: Suspend session tracking during initialization
    this.suspendSessionTracking = true;

    // PRIORITY 1: Check for session storage (temporary column adjustments)
    const hasSessionState = this.sessionStorageKey &&
      sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      // PRIORITY 2: Load saved views from backend
      await this.loadUserViews();

      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView = this.savedNamedViews &&
        ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        // PRIORITY 3: No session, no saved view - auto-size
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    // Hide loading screen - must trigger change detection since we're outside Angular's zone
    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  /**
   * Builds the list of available columns
   */
  private buildAvailableColumns(): void {
    // Start with all Party columns
    this.availableColumns = [...partyColumns.allColumns];

    // Filter out non-column objects
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    // Remove duplicates by colId
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    // Build and sort select list
    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  /**
   * Filters available columns based on user input
   */
  private _filterColumns(value: string | any): {value: any, viewValue: string}[] {
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option =>
      option.viewValue.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Display function for autocomplete - returns empty string to keep input clear
   */
  displayColumnFn(): string {
    return '';
  }

  ngAfterViewInit(): void {
    // Now it's safe to send the template to the service
    this.sidenavContentService.updateSidenavContent(this.partiesSidenavActions);
  }  

  ngOnDestroy(): void {
    // Clear the sidenav content when the component is destroyed
    this.sidenavContentService.updateSidenavContent(null);
  } 


  onCellClicked(event: any) {
    console.log('cell data--> ', event.data);
    console.log('vendor number: ', event.data.vendorNum);

    this.router.navigate(['/securehome/vendor', event.data.vendorNum]);

  }



  viewAllParties() {

    this.showPhysicianDirectory = false;
  }



  physicianDirectoryView() {

    this.filteredPhysicians = [];

    this.showPhysicianDirectory = true;

    const physician = 'Contracted Physician';

    // this.filteredPhysicians = this.globalDataService.allParties.filter( party => party.partyType === physician);
  }





  // View management methods (saveView, saveNamedViewNow, loadNamedView,
  // openSavedReportsDialog, openDeleteSavedViewsDialog, showSaveNamedView)
  // are inherited from EntityList base class

  selectionChanged(event) {

    this.gridApi.setColumnDefs(this.selectedCols);
    this.columnApi.setColumnState(this.selectedCols);

    this.fixColumnWidths();
  }







  findTextInTableData(filterValue) {
    this.gridApi.setQuickFilter(filterValue);
  }


  newVendorForm() {
    console.log('Routing to new vendor form. ');
    this.router.navigateByUrl('/securehome/new-vendor-full-page');
  }




  // Column management methods
  /**
   * Adds a column to the selected columns list
   */
  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  /**
   * Removes a column from the selected columns list
   */
  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  /**
   * Applies the selected columns to the grid
   */
  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  /**
   * Opens the customize columns dialog
   */
  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState
        .filter((col: any) => !col.hide)
        .map((col: any) => col.colId);

      this.selectedColumnsList = this.availableColumns.filter(col =>
        visibleColIds.includes(col.colId)
      );
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }

    this.customizeColumnsUi = true;
  }

  /**
   * Toggles the floating filter row on/off
   */
  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      this.gridApi.refreshHeader();
    }
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  async fixColumnWidths() {

    const delay = ms => new Promise(res => setTimeout(res, ms));

    while (this.gridApi == undefined) {
      await delay(1000); // 1 seconds
    }

    this.gridApi.sizeColumnsToFit();
    // this.autoSizeAll();
  }



}
